import { toORPCError } from '@orpc/client'
import { createRoute, useNavigate } from '@tanstack/react-router'
import { rootRoute } from '../__root'
import { OTPInput, REGEXP_ONLY_DIGITS, type SlotProps } from 'input-otp'
import { FormEvent, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { orpc } from '@/lib/orpc-client'
import { useAuthState } from '@/lib/auth-state'
import { GoogleIcon } from '@/components/icons/google-icon'
import { MicrosoftIcon } from '@/components/icons/microsoft-icon'
import { AuthBrandPanel } from '@/components/auth-brand-panel'
import { useTheme } from '@/lib/theme'
import { useLocale } from '@/i18n/locale'

export interface AuthSearchParams {
  invite?: string
  auth_error?: string
}

export const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth',
  component: AuthPage,
  validateSearch: (search: Record<string, unknown>): AuthSearchParams => ({
    invite: typeof search.invite === 'string' ? search.invite : undefined,
    auth_error: typeof search.auth_error === 'string' ? search.auth_error : undefined,
  }),
})

type AuthErrorReason =
  | 'INVALID_CHALLENGE'
  | 'INVALID_OTP'
  | 'OTP_EXPIRED'
  | 'OTP_ALREADY_USED'
  | 'OTP_ATTEMPTS_EXCEEDED'
  | 'RESEND_THROTTLED'
  | 'DOMAIN_NOT_ALLOWED'
  | 'EMAIL_DELIVERY_FAILED'
  | 'STALE_SESSION'

function getResendSecondsRemaining(resendAvailableAt: string | null): number {
  if (!resendAvailableAt) {
    return 0
  }

  const deltaMs = new Date(resendAvailableAt).getTime() - Date.now()
  return deltaMs <= 0 ? 0 : Math.ceil(deltaMs / 1_000)
}

function parseAuthError(error: unknown): {
  reason: AuthErrorReason | null
  resendAvailableAt: string | null
} {
  const orpcError = toORPCError(error)
  const data = (orpcError.data ?? {}) as Record<string, unknown>

  const reason = typeof data.reason === 'string' ? (data.reason as AuthErrorReason) : null
  const resendAvailableAt =
    typeof data.resendAvailableAt === 'string' ? data.resendAvailableAt : null

  return {
    reason,
    resendAvailableAt,
  }
}

type RequestErrorKey = `requestError.${'domainNotAllowed' | 'deliveryFailed' | 'generic'}`
type OtpErrorKey =
  | `otpError.${
      | 'invalid'
      | 'expired'
      | 'alreadyUsed'
      | 'attemptsExceeded'
      | 'invalidChallenge'
      | 'staleSession'
      | 'missingChallenge'
      | 'generic'}`
  | `resendError.${
      | 'throttled'
      | 'expired'
      | 'alreadyUsed'
      | 'attemptsExceeded'
      | 'invalidChallenge'
      | 'generic'}`
type NoticeKey = `notice.${'codeSent' | 'codeResent' | 'inviteInvalid' | 'staleSession'}`
type OAuthErrorKey = `oauthError.${
  | 'domainNotAllowed'
  | 'tokenExchangeFailed'
  | 'invalidState'
  | 'missingParams'
  | 'noEmail'
  | 'accessDenied'
  | 'generic'}`

function getRequestOtpErrorKey(reason: AuthErrorReason | null): RequestErrorKey {
  if (reason === 'DOMAIN_NOT_ALLOWED') {
    return 'requestError.domainNotAllowed'
  }
  if (reason === 'EMAIL_DELIVERY_FAILED') {
    return 'requestError.deliveryFailed'
  }
  return 'requestError.generic'
}

function getVerifyOtpErrorKey(reason: AuthErrorReason | null): OtpErrorKey {
  switch (reason) {
    case 'INVALID_OTP':
      return 'otpError.invalid'
    case 'OTP_EXPIRED':
      return 'otpError.expired'
    case 'OTP_ALREADY_USED':
      return 'otpError.alreadyUsed'
    case 'OTP_ATTEMPTS_EXCEEDED':
      return 'otpError.attemptsExceeded'
    case 'INVALID_CHALLENGE':
      return 'otpError.invalidChallenge'
    case 'STALE_SESSION':
      return 'otpError.staleSession'
    default:
      return 'otpError.generic'
  }
}

function getResendOtpErrorKey(reason: AuthErrorReason | null): OtpErrorKey {
  switch (reason) {
    case 'RESEND_THROTTLED':
      return 'resendError.throttled'
    case 'OTP_EXPIRED':
      return 'resendError.expired'
    case 'OTP_ALREADY_USED':
      return 'resendError.alreadyUsed'
    case 'OTP_ATTEMPTS_EXCEEDED':
      return 'resendError.attemptsExceeded'
    case 'INVALID_CHALLENGE':
      return 'resendError.invalidChallenge'
    default:
      return 'resendError.generic'
  }
}

const oauthErrorKeys: Record<string, OAuthErrorKey> = {
  domain_not_allowed: 'oauthError.domainNotAllowed',
  token_exchange_failed: 'oauthError.tokenExchangeFailed',
  invalid_state: 'oauthError.invalidState',
  missing_params: 'oauthError.missingParams',
  no_email: 'oauthError.noEmail',
  access_denied: 'oauthError.accessDenied',
}

function getOAuthErrorKey(errorCode: string): OAuthErrorKey {
  return oauthErrorKeys[errorCode] ?? 'oauthError.generic'
}

function OtpSlot({ slot }: { slot: SlotProps }) {
  return (
    <div
      className={cn(
        'h-12 w-10 rounded-md border text-center text-lg font-semibold leading-[48px] tabular-nums',
        slot.isActive ? 'border-foreground ring-2 ring-foreground/20' : 'border-border'
      )}
    >
      {slot.char ?? slot.placeholderChar ?? ''}
    </div>
  )
}

/**
 * The login page always renders in light mode (the brand panel provides the
 * dark half): force the light class while mounted and restore the user's
 * resolved theme on the way out. The no-flash script in index.html applies
 * the same rule before first paint.
 */
function useForceLightTheme() {
  const { resolved } = useTheme()
  useEffect(() => {
    const root = document.documentElement
    root.classList.remove('dark')
    root.classList.add('light')
    root.style.colorScheme = 'light'
    return () => {
      root.classList.toggle('dark', resolved === 'dark')
      root.classList.toggle('light', resolved === 'light')
      root.style.colorScheme = resolved
    }
  }, [resolved])
}

function AuthPage() {
  const navigate = useNavigate()
  const { t } = useTranslation(['auth', 'common'])
  useForceLightTheme()
  const { locale, setLocale } = useLocale()
  const { invite: inviteToken, auth_error: authError } = authRoute.useSearch()
  const { refresh } = useAuthState()
  const [step, setStep] = useState<'email' | 'otp'>('email')
  const [providers, setProviders] = useState<{ google: boolean; microsoft: boolean } | null>(null)
  const [email, setEmail] = useState('')
  const [challengeId, setChallengeId] = useState<string | null>(null)
  const [otpCode, setOtpCode] = useState('')
  const [resendAvailableAt, setResendAvailableAt] = useState<string | null>(null)
  const [devCode, setDevCode] = useState<string | null>(null)
  const [requestError, setRequestError] = useState<RequestErrorKey | null>(null)
  const [otpError, setOtpError] = useState<OtpErrorKey | null>(null)
  const [notice, setNotice] = useState<NoticeKey | null>(null)
  const [isRequesting, setIsRequesting] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isResending, setIsResending] = useState(false)
  const [tick, setTick] = useState(0)
  const [inviteInfo, setInviteInfo] = useState<{
    projectName: string
    inviterName: string | null
  } | null>(null)

  useEffect(() => {
    if (!inviteToken) return
    orpc.members
      .getInviteInfo({ token: inviteToken })
      .then((info) => {
        if (!info) {
          setNotice('notice.inviteInvalid')
          return
        }
        setInviteInfo({ projectName: info.projectName, inviterName: info.inviterName })
        setEmail(info.email)
      })
      .catch(() => {
        // Ignore — invite lookup is optional UX
      })
  }, [inviteToken])

  useEffect(() => {
    orpc.auth
      .providers({})
      .then((result) => setProviders({ google: result.google, microsoft: result.microsoft }))
      .catch(() => setProviders(null))
  }, [])

  useEffect(() => {
    const authNotice = window.sessionStorage.getItem('auth_notice')
    if (authNotice === 'stale-session') {
      setNotice('notice.staleSession')
      window.sessionStorage.removeItem('auth_notice')
    }
  }, [])

  useEffect(() => {
    if (!resendAvailableAt) {
      return
    }

    const interval = window.setInterval(() => {
      setTick((value) => value + 1)
    }, 1_000)

    return () => {
      window.clearInterval(interval)
    }
  }, [resendAvailableAt])

  const resendSecondsRemaining = useMemo(
    () => getResendSecondsRemaining(resendAvailableAt),
    [resendAvailableAt, tick]
  )

  const handleRequestOtp = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setRequestError(null)
    setOtpError(null)
    setNotice(null)
    setIsRequesting(true)

    try {
      const result = await orpc.auth.requestOtp({ email })
      setChallengeId(result.challengeId)
      setResendAvailableAt(result.resendAvailableAt)
      setDevCode(result.devCode ?? null)
      setStep('otp')
      setOtpCode('')
      setNotice('notice.codeSent')
    } catch (error) {
      console.log('error', error)
      const { reason } = parseAuthError(error)
      setRequestError(getRequestOtpErrorKey(reason))
    } finally {
      setIsRequesting(false)
    }
  }

  const verifyOtp = async (code: string) => {
    if (!challengeId || isVerifying) {
      if (!challengeId) {
        setOtpError('otpError.missingChallenge')
      }
      return
    }

    setOtpError(null)
    setNotice(null)
    setIsVerifying(true)

    try {
      await orpc.auth.verifyOtp({
        challengeId,
        code,
      })
      await refresh()
      await navigate({ to: '/' })
    } catch (error) {
      const { reason } = parseAuthError(error)
      if (reason === 'INVALID_CHALLENGE') {
        setStep('email')
        setChallengeId(null)
        setResendAvailableAt(null)
      }
      setOtpError(getVerifyOtpErrorKey(reason))
    } finally {
      setIsVerifying(false)
    }
  }

  const handleVerifyOtp = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    verifyOtp(otpCode)
  }

  const handleResendOtp = async () => {
    if (!challengeId || resendSecondsRemaining > 0) {
      return
    }

    setOtpError(null)
    setNotice(null)
    setIsResending(true)

    try {
      const result = await orpc.auth.resendOtp({ challengeId })
      setResendAvailableAt(result.resendAvailableAt)
      setDevCode(result.devCode ?? null)
      setNotice('notice.codeResent')
    } catch (error) {
      const { reason, resendAvailableAt: nextResendAvailableAt } = parseAuthError(error)
      if (nextResendAvailableAt) {
        setResendAvailableAt(nextResendAvailableAt)
      }
      if (reason === 'INVALID_CHALLENGE') {
        setStep('email')
        setChallengeId(null)
        setResendAvailableAt(null)
      }
      setOtpError(getResendOtpErrorKey(reason))
    } finally {
      setIsResending(false)
    }
  }

  return (
    <main className="flex min-h-screen">
      {/* ── Left: Form panel ── */}
      <div className="relative flex w-full lg:w-[46%] flex-col justify-center bg-background px-8 py-12 sm:px-12 lg:px-16">
        {/* Language switch — Swedish is the default; one click to the other. */}
        <button
          type="button"
          onClick={() => setLocale(locale === 'sv' ? 'en' : 'sv')}
          className="absolute bottom-5 left-8 text-[11px] text-muted-foreground underline-offset-2 hover:text-foreground hover:underline sm:left-12 lg:left-16"
        >
          {locale === 'sv' ? t('common:language.en') : t('common:language.sv')}
        </button>
        <div className="mx-auto w-full max-w-[380px]">
          {/* Logo */}
          <a href="/" className="mb-10 flex items-center gap-2 text-foreground no-underline">
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinejoin="round"
              />
            </svg>
            <span className="text-sm font-bold tracking-[0.05em] text-foreground">
              {t('login.brand')}
            </span>
          </a>

          {/* Heading */}
          <h1 className="mb-1.5 text-[22px] font-bold tracking-tight text-foreground">
            {t('login.title')}
          </h1>
          <p className="mb-8 text-[13px] leading-relaxed text-muted-foreground">
            {inviteInfo
              ? t('login.inviteHint', { project: inviteInfo.projectName })
              : step === 'email'
                ? t('login.emailHint')
                : t('login.otpHint', { email })}
          </p>

          {authError && (
            <p className="mb-4 text-sm text-destructive">{t(getOAuthErrorKey(authError))}</p>
          )}

          {step === 'email' ? (
            <form onSubmit={handleRequestOtp}>
              <label
                className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground"
                htmlFor="auth-email"
              >
                {t('login.emailLabel')}
              </label>
              <input
                id="auth-email"
                type="email"
                required
                autoComplete="email"
                placeholder={t('login.emailPlaceholder')}
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                className="mb-3.5 h-10 w-full rounded-[7px] border border-input bg-card px-3.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-foreground transition-colors"
              />

              {requestError && <p className="mb-3 text-sm text-destructive">{t(requestError)}</p>}
              {notice && <p className="mb-3 text-sm text-muted-foreground">{t(notice)}</p>}

              <Button
                className="mb-4 w-full rounded-[7px] bg-[#1a1a1a] text-[13px] font-bold tracking-[0.07em] text-white hover:bg-[#2e2e2e]"
                disabled={isRequesting}
                type="submit"
              >
                {isRequesting ? t('login.sending') : t('login.continue')}
              </Button>
            </form>
          ) : null}

          {step === 'email' && providers && (providers.google || providers.microsoft) && (
            <>
              {/* Divider */}
              <div className="mb-3.5 flex items-center gap-2.5">
                <div className="h-px flex-1 bg-border" />
                <span className="text-[10px] tracking-[0.12em] text-muted-foreground/70">
                  {t('login.or')}
                </span>
                <div className="h-px flex-1 bg-border" />
              </div>

              <div className="space-y-2">
                {providers.google && (
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-[7px] border border-[#e0ddd8] bg-white px-3 py-2.5 text-xs text-[#333] transition-colors hover:border-[#ccc] hover:bg-[#f5f4f1] cursor-pointer"
                    onClick={() => {
                      const params = new URLSearchParams()
                      if (email) params.set('login_hint', email)
                      if (inviteToken) params.set('redirect_after', `/auth?invite=${inviteToken}`)
                      window.location.href = `/auth/google/login?${params.toString()}`
                    }}
                  >
                    <GoogleIcon className="h-4 w-4" />
                    {t('login.continueWithGoogle')}
                  </button>
                )}
                {providers.microsoft && (
                  <button
                    type="button"
                    className="flex w-full items-center justify-center gap-2 rounded-[7px] border border-[#e0ddd8] bg-white px-3 py-2.5 text-xs text-[#333] transition-colors hover:border-[#ccc] hover:bg-[#f5f4f1] cursor-pointer"
                    onClick={() => {
                      const params = new URLSearchParams()
                      if (email) params.set('login_hint', email)
                      if (inviteToken) params.set('redirect_after', `/auth?invite=${inviteToken}`)
                      window.location.href = `/auth/microsoft/login?${params.toString()}`
                    }}
                  >
                    <MicrosoftIcon className="h-4 w-4" />
                    {t('login.continueWithMicrosoft')}
                  </button>
                )}
              </div>
            </>
          )}

          {step === 'otp' && (
            <form className="space-y-4" onSubmit={handleVerifyOtp}>
              <OTPInput
                maxLength={6}
                pattern={REGEXP_ONLY_DIGITS}
                value={otpCode}
                onChange={setOtpCode}
                onComplete={verifyOtp}
                containerClassName="justify-center gap-2"
                render={({ slots }) => (
                  <div className="flex justify-center gap-2">
                    {slots.map((slot, index) => (
                      <OtpSlot key={index} slot={slot} />
                    ))}
                  </div>
                )}
              />

              {/* Local dev only — the server sends it just with DEV_SHOW_OTP=1 off a deploy. */}
              {devCode && (
                <p data-testid="dev-otp" className="text-center text-xs text-muted-foreground">
                  {t('login.devCode', { code: devCode })}
                </p>
              )}

              {otpError && <p className="text-sm text-destructive">{t(otpError)}</p>}
              {notice && <p className="text-sm text-muted-foreground">{t(notice)}</p>}

              <Button
                className="w-full rounded-[7px] bg-[#1a1a1a] text-[13px] font-bold tracking-[0.07em] text-white hover:bg-[#2e2e2e]"
                disabled={isVerifying || otpCode.length !== 6}
                type="submit"
              >
                {isVerifying ? t('login.verifying') : t('login.verify')}
              </Button>

              <div className="flex items-center justify-between">
                <Button
                  type="button"
                  variant="link"
                  className="px-0"
                  onClick={() => {
                    setStep('email')
                    setOtpCode('')
                    setChallengeId(null)
                    setResendAvailableAt(null)
                    setOtpError(null)
                  }}
                >
                  {t('login.changeEmail')}
                </Button>
                <Button
                  type="button"
                  variant="link"
                  className="px-0"
                  onClick={handleResendOtp}
                  disabled={isResending || resendSecondsRemaining > 0}
                >
                  {resendSecondsRemaining > 0
                    ? t('login.resendIn', { seconds: resendSecondsRemaining })
                    : isResending
                      ? t('login.resending')
                      : t('login.resend')}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>

      {/* ── Right: Brand panel (hidden on mobile) ── */}
      <AuthBrandPanel />
    </main>
  )
}
